import React from 'react';
import { Button, Select, Tooltip, theme } from 'antd';
import { HolderOutlined, PlusOutlined, WarningFilled } from '@ant-design/icons';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { avisosDeTarea, duracionDeTarea } from './ganttGeometry';
import { ROW_HEIGHT, HEADER_HEIGHT, LEFT_WIDTH } from './ganttLayout';

const restrictToVerticalAxis = ({ transform }) => ({ ...transform, x: 0 });

// Anchos de las columnas de la lista. Suman LEFT_WIDTH.
const W_HANDLE = 32;
const W_RESP = 150;
const W_DURACION = 72;
const W_ACCION = 36;

// Fila arrastrable (mismo patrón que SortableTabsTable): el handle recibe los
// listeners; el resto de las celdas queda editable. Altura fija = ROW_HEIGHT para
// que quede alineada con la fila de la línea de tiempo.
function FilaTarea({
  tarea,
  avisos,
  duracion,
  especialistas,
  readOnly,
  onEditar,
  onAgregarSubtarea,
}) {
  const { token } = theme.useToken();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tarea.id,
  });
  const esSubtarea = tarea.parent_id != null;

  return (
    <div
      ref={setNodeRef}
      data-testid="gantt-fila"
      data-tarea-id={tarea.id}
      style={{
        height: ROW_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingRight: 8,
        boxSizing: 'border-box',
        borderBottom: `1px solid ${token.colorFillQuaternary}`,
        background: token.colorBgContainer,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { zIndex: 9999, position: 'relative', boxShadow: token.boxShadow } : {}),
      }}
    >
      <div style={{ width: W_HANDLE, flex: 'none', textAlign: 'center' }}>
        <Button
          type="text"
          size="small"
          icon={<HolderOutlined />}
          disabled={readOnly}
          style={{ cursor: 'grab', touchAction: 'none' }}
          {...attributes}
          {...listeners}
          aria-label="Reordenar tarea"
        />
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          paddingLeft: esSubtarea ? 18 : 0,
        }}
      >
        {avisos.length > 0 ? (
          <Tooltip title={avisos.join(' · ')}>
            <WarningFilled style={{ color: token.colorError, flex: 'none' }} />
          </Tooltip>
        ) : null}
        <span
          title={tarea.description}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: esSubtarea ? 400 : 500,
          }}
        >
          {tarea.description}
        </span>
      </div>

      <Select
        value={tarea.assigned_to_user_id ?? undefined}
        size="small"
        allowClear
        showSearch
        placeholder="Sin asignar"
        disabled={readOnly}
        style={{ width: W_RESP, flex: 'none' }}
        optionFilterProp="label"
        onChange={(next) => onEditar(tarea.id, 'assigned_to_user_id', next ?? null)}
        options={especialistas.map((u) => ({ value: u.id, label: `${u.fullname}` }))}
      />
      <span
        data-testid="gantt-duracion"
        style={{ width: W_DURACION, flex: 'none', fontSize: 12, color: token.colorTextSecondary }}
      >
        {duracion == null ? '—' : duracion === 1 ? '1 día' : `${duracion} días`}
      </span>
      <div style={{ width: W_ACCION, flex: 'none', textAlign: 'center' }}>
        {readOnly || esSubtarea ? null : (
          <Tooltip title="Agregar subtarea">
            <Button
              type="text"
              size="small"
              icon={<PlusOutlined />}
              aria-label="Agregar subtarea"
              onClick={() => onAgregarSubtarea?.(tarea.id)}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

// Mitad izquierda de la carta Gantt: la lista de tareas editable. Es una columna
// fija (pegada a la izquierda mientras la línea de tiempo se desplaza) de filas con
// altura fija, para alinearse con las barras sin cálculos.
// Props:
//   tareas, dependencias, fechaComprometida — datos del summary.
//   especialistas — [{ id, fullname, rut }] para el responsable.
//   readOnly — ODT finalizada (sin editar, sin reordenar, sin subtareas).
//   onEditarItem(idItem, campos) — persiste un campo de una tarea.
//   onReordenar(tareasOrdenadas) — persiste el orden nuevo.
//   onAgregarSubtarea(idPadre) — crea una subtarea de esa fila.
export default function GanttTaskTable({
  tareas = [],
  dependencias = [],
  fechaComprometida = null,
  especialistas = [],
  readOnly = false,
  onEditarItem,
  onReordenar,
  onAgregarSubtarea,
}) {
  const { token } = theme.useToken();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const editarCampo = (id, campo, valor) => {
    if (readOnly || !onEditarItem) return;
    onEditarItem(id, { [campo]: valor });
  };

  const handleDragEnd = ({ active, over }) => {
    if (readOnly || !over || active.id === over.id || !onReordenar) return;
    const oldIndex = tareas.findIndex((t) => t.id === active.id);
    const newIndex = tareas.findIndex((t) => t.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReordenar(arrayMove(tareas, oldIndex, newIndex));
  };

  const titulo = {
    fontSize: 12,
    fontWeight: 600,
    color: token.colorTextSecondary,
    flex: 'none',
  };

  return (
    <div
      style={{
        position: 'sticky',
        left: 0,
        zIndex: 6,
        width: LEFT_WIDTH,
        flex: 'none',
        background: token.colorBgContainer,
        borderRight: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 7,
          height: HEADER_HEIGHT,
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'flex-end',
          gap: 6,
          padding: '0 8px 8px 0',
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <span style={{ width: W_HANDLE, flex: 'none' }} />
        <span style={{ ...titulo, flex: 1 }}>Tarea</span>
        <span style={{ ...titulo, width: W_RESP }}>Responsable</span>
        <span style={{ ...titulo, width: W_DURACION }}>Duración</span>
        <span style={{ width: W_ACCION, flex: 'none' }} />
      </div>

      {tareas.length === 0 ? (
        <div style={{ padding: 16, color: token.colorTextTertiary }}>La ODT no tiene tareas.</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={tareas.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            {tareas.map((t) => (
              <FilaTarea
                key={t.id}
                tarea={t}
                avisos={avisosDeTarea(t, tareas, dependencias, fechaComprometida)}
                duracion={duracionDeTarea(t, tareas)}
                especialistas={especialistas}
                readOnly={readOnly}
                onEditar={editarCampo}
                onAgregarSubtarea={onAgregarSubtarea}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
